// src/services/webReportsService.js

const API_BASE_URL = process.env.REACT_APP_API_URL1; // e.g. http://localhost:3000

const getTenant = () => localStorage.getItem("tenant") || "";

const buildQs = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v) !== "") qs.set(k, v);
  });
  return qs.toString();
};

export async function fetchManualRequests({ date_from, date_to, status, staff_id, staff_code, employee_search }) {
  const tenant = getTenant();
  const qs = buildQs({ date_from, date_to, status, staff_id, staff_code, employee_search });

  const res = await fetch(`${API_BASE_URL}/web-reports/manual-requests?${qs}`, {
    credentials: "include", // ✅ MUST be here (not in headers)
    headers: {
      "X-Tenant": tenant,    // ✅ match your standard
      Accept: "application/json",
    },
  });

  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  return JSON.parse(text);
}

export function buildManualExcelUrl({ date_from, date_to, status, staff_id, staff_code, employee_search }) {
  const qs = buildQs({ date_from, date_to, status, staff_id, staff_code, employee_search });
  return `${API_BASE_URL}/web-reports/manual-requests/excel?${qs}`;
}

export async function fetchLeaveSummary({ date_from, date_to, branch_id, org_node_id, min_days }) {
  const tenant = getTenant();
  const qs = buildQs({ date_from, date_to, branch_id, org_node_id, min_days });

  const res = await fetch(`${API_BASE_URL}/web-reports/leave-summary?${qs}`, {
    credentials: "include",
    headers: {
      "X-Tenant": tenant,
      Accept: "application/json",
    },
  });

  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  return JSON.parse(text);
}

export function buildLeaveSummaryExcelUrl({ date_from, date_to, branch_id, org_node_id, min_days }) {
  const qs = buildQs({ date_from, date_to, branch_id, org_node_id, min_days });
  return `${API_BASE_URL}/web-reports/leave-summary/excel?${qs}`;
}

export async function fetchLeaveHistoryAudit(params) {
  const tenant = getTenant();
  const qs = buildQs(params);
  const res = await fetch(`${API_BASE_URL}/web-reports/leave-history-audit?${qs}`, {
    credentials: "include",
    headers: { "X-Tenant": tenant, Accept: "application/json" },
  });
  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  return JSON.parse(text);
}

export function buildLeaveHistoryAuditExcelUrl(params) {
  return `${API_BASE_URL}/web-reports/leave-history-audit/excel?${buildQs(params)}`;
}

export async function fetchAttendanceMatrix({ date_from, date_to, branch_id, org_node_id }) {
  const tenant = getTenant();
  const qs = buildQs({ date_from, date_to, branch_id, org_node_id });

  const res = await fetch(`${API_BASE_URL}/web-reports/attendance-matrix?${qs}`, {
    credentials: "include",
    headers: {
      "X-Tenant": tenant,
      Accept: "application/json",
    },
  });

  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  return JSON.parse(text);
}

export function buildAttendanceMatrixExcelUrl({ date_from, date_to, branch_id, org_node_id, show_total_days, show_expected_days, show_present_days, show_leave_days }) {
  const qs = buildQs({ date_from, date_to, branch_id, org_node_id, show_total_days, show_expected_days, show_present_days, show_leave_days });
  return `${API_BASE_URL}/web-reports/attendance-matrix/excel?${qs}`;
}

async function fetchDetailReport(path, params) {
  const tenant = getTenant();
  const qs = buildQs(params);
  const res = await fetch(`${API_BASE_URL}/web-reports/${path}?${qs}`, {
    credentials: "include",
    headers: { "X-Tenant": tenant, Accept: "application/json" },
  });
  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  return JSON.parse(text);
}

export const fetchUnpaidAbsenceReport = (params) => fetchDetailReport("unpaid-absences", params);

export const fetchEducationQualificationsReport = (params) => fetchDetailReport("education-qualifications", params);

export async function fetchCustomPersonnel({ branch_id, org_node_id, fields }) {
  const tenant = getTenant();
  const qs = buildQs({ branch_id, org_node_id, fields });

  const res = await fetch(`${API_BASE_URL}/web-reports/custom-personnel?${qs}`, {
    credentials: "include",
    headers: {
      "X-Tenant": tenant,
      Accept: "application/json",
    },
  });

  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  return JSON.parse(text);
}

export function buildCustomPersonnelExcelUrl({ branch_id, org_node_id, fields }) {
  const qs = buildQs({ branch_id, org_node_id, fields });
  return `${API_BASE_URL}/web-reports/custom-personnel/excel?${qs}`;
}

export async function fetchStaffMovement({ date_from, date_to, branch_id, org_node_id }) {
  const tenant = getTenant();
  const qs = buildQs({ date_from, date_to, branch_id, org_node_id });

  const res = await fetch(`${API_BASE_URL}/web-reports/staff-movement?${qs}`, {
    credentials: "include",
    headers: {
      "X-Tenant": tenant,
      Accept: "application/json",
    },
  });

  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  return JSON.parse(text);
}

export function buildStaffMovementExcelUrl({ date_from, date_to, branch_id, org_node_id }) {
  const qs = buildQs({ date_from, date_to, branch_id, org_node_id });
  return `${API_BASE_URL}/web-reports/staff-movement/excel?${qs}`;
}

export async function fetchTurnoverReport({ year, branch_id, org_node_id }) {
  const tenant = getTenant();
  const qs = buildQs({ year, branch_id, org_node_id });

  const res = await fetch(`${API_BASE_URL}/web-reports/turnover?${qs}`, {
    credentials: "include",
    headers: {
      "X-Tenant": tenant,
      Accept: "application/json",
    },
  });

  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  return JSON.parse(text);
}

export async function fetchDailyAttendanceSummary({ date, branch_id, org_node_id }) {
  const tenant = getTenant();
  const qs = buildQs({ date, branch_id, org_node_id });

  const res = await fetch(`${API_BASE_URL}/web-reports/daily-attendance-summary?${qs}`, {
    credentials: "include",
    headers: {
      "X-Tenant": tenant,
      Accept: "application/json",
    },
  });

  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  return JSON.parse(text);
}

export function buildDailyAttendanceSummaryExcelUrl({ date, branch_id, org_node_id }) {
  const qs = buildQs({ date, branch_id, org_node_id });
  return `${API_BASE_URL}/web-reports/daily-attendance-summary/excel?${qs}`;
}

export async function fetchLeaveCalendar({ month, branch_id, org_node_id }) {
  const tenant = getTenant();
  const qs = buildQs({ month, branch_id, org_node_id });

  const res = await fetch(`${API_BASE_URL}/web-reports/leave-calendar?${qs}`, {
    credentials: "include",
    headers: {
      "X-Tenant": tenant,
      Accept: "application/json",
    },
  });

  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  return JSON.parse(text);
}

export async function fetchOtSubmissionAuditReport({ date_from, date_to, source, status, employee_search }) {
  const tenant = getTenant();
  const qs = buildQs({ date_from, date_to, source, status, employee_search });

  const res = await fetch(`${API_BASE_URL}/web-reports/ot-submission-audit?${qs}`, {
    credentials: "include",
    headers: {
      "X-Tenant": tenant,
      Accept: "application/json",
    },
  });

  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  return JSON.parse(text);
}
